
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { supabase } from '@/integrations/supabase/client';

interface UnifiedUserData {
  profile: {
    id: string;
    plan_type: string;
    credits_remaining: number;
    monthly_limit: number;
    image_generations_used: number;
    full_name: string;
    email: string;
  } | null;
  loading: boolean;
  error: string | null;
}

// Secure global state management
let globalUserData: UnifiedUserData = {
  profile: null,
  loading: true,
  error: null
};

let subscribers: Set<(data: UnifiedUserData) => void> = new Set();
let fetchPromise: Promise<void> | null = null;
let lastFetch = 0;
const CACHE_DURATION = 30000; // 30 seconds cache for better performance cache

const notifySubscribers = () => {
  subscribers.forEach(callback => {
    try {
      callback(globalUserData);
    } catch (error) {
      console.error('Error in subscriber callback:', error);
    }
  });
};

const fetchUserData = async (userId: string, forceRefresh = false): Promise<void> => {
  const now = Date.now();
  
  // Use cache if recent and not forcing refresh
  if (!forceRefresh && (now - lastFetch) < CACHE_DURATION && globalUserData.profile) {
    return;
  }

  // Prevent multiple simultaneous fetches
  if (fetchPromise && !forceRefresh) {
    return fetchPromise;
  }

  fetchPromise = (async () => {
    try {
      globalUserData = { ...globalUserData, loading: true, error: null };
      notifySubscribers();

      console.log('Fetching unified user data...');

      // Fetch profile data
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, plan_type, credits_remaining, monthly_limit, image_generations_used, full_name, email')
        .eq('id', userId)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Error fetching profile:', profileError);
      }

      if (!profile) {
        // No profile yet; proceed without blocking (UI will handle null profile)
        globalUserData = {
          profile: null,
          loading: false,
          error: null
        };
      } else {
        // Fetch subscriber data for accurate usage tracking
        const { data: subscriberData, error: subscriberError } = await supabase
          .from('subscribers')
          .select('monthly_usage, plan_type')
          .eq('user_id', userId)
          .maybeSingle();

        if (subscriberError && subscriberError.code !== 'PGRST116') {
          console.error('Error fetching subscriber data:', subscriberError);
        }

        // Prefer profile counters updated by DB triggers; fallback to subscriber data if needed
        const planType = subscriberData?.plan_type || profile.plan_type || 'free';
        const monthlyLimit = Math.max(1, profile.monthly_limit || 250);

        const profileUsage = profile.image_generations_used ?? 0;
        const subscriberUsage = subscriberData?.monthly_usage ?? 0;
        const actualUsage = profileUsage > 0 ? profileUsage : subscriberUsage;

        // Use profile.credits_remaining when available (maintained by triggers)
        const creditsRemaining = Number.isFinite(profile.credits_remaining)
          ? profile.credits_remaining
          : Math.max(0, monthlyLimit - actualUsage);
        // Sanitize and validate profile data
        globalUserData = {
          profile: profile ? {
            id: profile.id,
            plan_type: planType,
            credits_remaining: creditsRemaining,
            monthly_limit: monthlyLimit,
            image_generations_used: actualUsage,
            full_name: (profile.full_name || '').trim(),
            email: (profile.email || '').trim()
          } : null,
          loading: false,
          error: null
        };
        lastFetch = now;
        console.log('User data updated:', globalUserData.profile);
      }

      notifySubscribers();
    } catch (error: any) {
      console.error('Error in fetchUserData:', error);
      globalUserData = {
        profile: null,
        loading: false,
        error: 'Failed to load user data'
      };
      notifySubscribers();
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
};

// Secure real-time subscription setup
let realtimeChannel: any = null;

const setupRealtimeSubscription = (userId: string) => {
  if (realtimeChannel) {
    supabase.removeChannel(realtimeChannel);
  }

  realtimeChannel = supabase.channel(`user-data-${userId}-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  
  realtimeChannel
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'profiles',
      filter: `id=eq.${userId}`
    }, (payload) => {
      console.log('Profile updated via real-time:', payload);
      // Force refresh when profile changes
      fetchUserData(userId, true);
    })
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'subscribers',
      filter: `user_id=eq.${userId}`
    }, (payload) => {
      console.log('Subscriber data updated via real-time:', payload);
      // Force refresh when subscriber data changes
      fetchUserData(userId, true);
    })
    .subscribe((status) => {
      console.log('Real-time subscription status:', status);
    });
};

export const useUnifiedUserData = () => {
  const { user } = useSecureAuthContext();
  const [data, setData] = useState<UnifiedUserData>(globalUserData);
  const mountedRef = useRef(true);

  const refetch = useCallback(() => {
    if (user?.id) {
      fetchUserData(user.id, true);
    }
  }, [user?.id]);

  useEffect(() => {
    mountedRef.current = true;

    if (user?.id) {
      // Subscribe to updates
      const unsubscribe = (newData: UnifiedUserData) => {
        if (mountedRef.current) {
          setData(newData);
        }
      };
      
      subscribers.add(unsubscribe);
      
      // Set initial data
      setData(globalUserData);
      
      // Fetch data if needed
      fetchUserData(user.id);
      
      // Setup secure real-time subscription
      setupRealtimeSubscription(user.id);

      return () => {
        mountedRef.current = false;
        subscribers.delete(unsubscribe);
      };
    } else {
      setData({ profile: null, loading: false, error: null });
    }

    return () => {
      mountedRef.current = false;
    };
  }, [user?.id]);

  return {
    ...data,
    refetch
  };
};

// Secure utility functions for credit management
export const updateCredits = async (userId: string, newCredits: number) => {
  try {
    // Validate input
    const sanitizedCredits = Math.max(0, Math.floor(newCredits));
    
    const { error } = await supabase
      .from('profiles')
      .update({ credits_remaining: sanitizedCredits })
      .eq('id', userId);

    if (error) {
      console.error('Error updating credits:', error);
      return false;
    }

    // Update global state immediately for instant UI update
    if (globalUserData.profile) {
      globalUserData.profile.credits_remaining = sanitizedCredits;
      notifySubscribers();
    }

    return true;
  } catch (error) {
    console.error('Error in updateCredits:', error);
    return false;
  }
};

export const incrementImageCount = async (userId: string) => {
  try {
    // Use the unified increment_user_usage function for consistency
    const { error } = await supabase.rpc('increment_user_usage', {
      user_id: userId,
      count: 1
    });

    if (error) {
      console.error('Error incrementing image count:', error);
      return false;
    }

    // Update global state immediately for instant UI update
    if (globalUserData.profile) {
      const newUsed = globalUserData.profile.image_generations_used + 1;
      const newCredits = Math.max(0, globalUserData.profile.monthly_limit - newUsed);
      
      globalUserData.profile.image_generations_used = newUsed;
      globalUserData.profile.credits_remaining = newCredits;
      notifySubscribers();
    }

    return true;
  } catch (error) {
    console.error('Error in incrementImageCount:', error);
    return false;
  }
};
